"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Cable, Lock, ShieldCheck, Waves } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Feature = {
  spec: string;
  title: string;
  description: string;
  statValue: number;
  statSuffix: string;
  statPrefix: string;
  statLabel: string;
  iconKey: string;
};
const features: Feature[] = [{
  spec: "SPEC.001 / PRECISION",
  title: "Industrial Laser Precision",
  description: "High-tolerance laser engraving systems ensure permanent, legible identification for critical industrial components.",
  statValue: 0.01,
  statSuffix: "mm",
  statPrefix: "±",
  statLabel: "tolerance",
  iconKey: "fiber"
}, {
  spec: "SPEC.002 / DURABILITY",
  title: "Extreme Environment Resilience",
  description: "Materials engineered to withstand chemical exposure, high heat, and mechanical abrasion in harsh field conditions.",
  statValue: 20,
  statSuffix: "+ yrs",
  statPrefix: "",
  statLabel: "service life",
  iconKey: "acoustic"
}, {
  spec: "SPEC.003 / COMPLIANCE",
  title: "Regulatory Standard Adherence",
  description: "Full compliance with NFPA 70E and OSHA labeling requirements for safety, arc flash, and equipment identification.",
  statValue: 100,
  statSuffix: "%",
  statPrefix: "",
  statLabel: "compliant",
  iconKey: "power"
}, {
  spec: "SPEC.004 / TRACEABILITY",
  title: "Advanced DPM Traceability",
  description: "Direct Part Marking solutions providing unique identification for full lifecycle tracking and quality assurance.",
  statValue: 99.9,
  statSuffix: "%",
  statPrefix: "",
  statLabel: "read accuracy",
  iconKey: "access"
}];
function CountUp({
  value,
  prefix,
  suffix,
  isVisible
}: {
  value: number;
  prefix: string;
  suffix: string;
  isVisible: boolean;
}) {
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || startedRef.current) return;
    startedRef.current = true;
    const duration = 1400;
    const start = performance.now();
    const isFloat = !Number.isInteger(value);
    let raf = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = value * eased;
      setDisplay(isFloat ? Math.round(current * 100) / 100 : Math.round(current));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isVisible, value]);
  return <span className="font-mono tabular-nums">
      {prefix}
      {display}
      {suffix}
    </span>;
}
function FeatureIcon({
  iconKey
}: {
  iconKey: string;
}) {
  const baseClass = "text-primary transition-transform duration-500 group-hover:scale-105";
  if (iconKey === "fiber") {
    return <Cable className={`${baseClass} size-10`} strokeWidth={1.25} />;
  }
  if (iconKey === "acoustic") {
    return <Waves className={`${baseClass} size-10 group-hover:animate-pulse`} strokeWidth={1.25} />;
  }
  if (iconKey === "power") {
    return <ShieldCheck className={`${baseClass} size-10`} strokeWidth={1.25} />;
  }
  return <Lock className={`${baseClass} size-10`} strokeWidth={1.25} />;
}
function FeatureCard({
  feature,
  index
}: {
  feature: Feature;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div data-index={index} ref={ref} className={`group relative flex flex-col justify-between min-h-[320px] rounded-lg border border-border bg-card p-8 sm:p-10 transition-all duration-500 hover:border-primary hover:-translate-y-0.5 overflow-hidden shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionDelay: `${index * 80}ms`
  }}>
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <FeatureIcon iconKey={feature.iconKey} />
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
            0{index + 1}
          </span>
        </div>
        <div className="flex flex-col gap-3">
          <span className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
            {feature.spec}
          </span>
          <h3 className="font-default text-[22px] font-medium leading-tight text-foreground">
            {feature.title}
          </h3>
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-4">
        <p className="text-sm leading-relaxed text-muted-foreground">
          {feature.description}
        </p>
        <div className="flex items-baseline gap-2 pt-2">
          <span className="text-2xl font-default font-semibold text-primary">
            <CountUp value={feature.statValue} prefix={feature.statPrefix} suffix={feature.statSuffix} isVisible={isVisible} />
          </span>
          <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
            {feature.statLabel}
          </span>
        </div>
      </div>
      <span className="absolute bottom-0 left-0 h-px w-0 bg-primary transition-all duration-500 group-hover:w-full" />
    </div>;
}
export default function WhyEvermarkAcrossIndustries() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="2353" id="why-evermark-across-industries" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px] px-6 md:px-10">
      <div className="mx-auto max-w-7xl">
        <div ref={headerRef} className={`flex flex-col gap-6 max-w-4xl transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs tracking-widest text-primary uppercase">INDUSTRIAL EXCELLENCE</span>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-[56px] font-medium leading-[1.05] tracking-tight text-foreground">
            Engineered for Industrial Reliability
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-[540px]">
            From precision laser engraving to durable DPM solutions, Evermark provides 
            the identification infrastructure required for mission-critical industrial operations.
          </p>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {features.slice(0, 2).map((feature, i) => <FeatureCard key={feature.spec} feature={feature} index={i} />)}
          <div className="hidden lg:contents">
            {features.slice(2, 4).map((feature, i) => <FeatureCard key={feature.spec} feature={feature} index={i + 2} />)}
          </div>
        </div>

        <div className="hidden sm:flex lg:hidden items-center gap-6 my-8">
          <Separator className="flex-1 bg-border" />
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
            Continued
          </span>
          <Separator className="flex-1 bg-border" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4 md:gap-5">
          {features.slice(2, 4).map((feature, i) => <FeatureCard key={feature.spec} feature={feature} index={i + 2} />)}
        </div>

        <div className="mt-20 flex justify-center">
          
        </div>
      </div>
    </section>;
}